import * as React from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { useLocalization } from '@progress/kendo-react-intl';
import { Input } from '@progress/kendo-react-inputs';
import {
    ListBox, ListBoxToolbar,
    processListBoxDragAndDrop, ListBoxToolbarClickEvent,
    ListBoxDragEvent, ListBoxItemClickEvent
} from '@progress/kendo-react-listbox';
import { filterClearIcon } from '@progress/kendo-svg-icons';
import { getEndpointUniqueKey } from '../../Utilities/AssetGreasePumpUtils';
import { enMessages } from '../../../../messages/en-US';

interface Props {
    groups: any[];
    selectedGreasePumpId: string | null;
    selectedGreasePumpName: string | null;
    unassignedEndpoints: any[];
    assignedEndpoints: any[];
    onAssignEndpoints: (endpointKeys: string[]) => void;
    onUnassignEndpoints: (endpointKeys: string[]) => void;
}

const SELECTED_FIELD = 'selected';
const UNIQUE_KEY_FIELD = 'uniqueKey';

const sortEndpointsByName = (list: any[]) =>
    [...list].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

const addListBoxFields = (endpoints: any[]) =>
    endpoints.map(ep => ({
        ...ep,
        [SELECTED_FIELD]: ep[SELECTED_FIELD] ?? false,
        [UNIQUE_KEY_FIELD]: ep[UNIQUE_KEY_FIELD] ?? getEndpointUniqueKey(ep)
    }));

const MyCustomItem = (props: any) => {
    const { dataItem, ...others } = props;
    const getGroupName = (groupId: string) => {
        const group = props.groups?.find((g: any) => g.id === groupId);
        return group?.name || groupId;
    };
    return (
        <li {...others}>
            <div>
                <span style={{ fontWeight: 'bold' }}>{dataItem.name}</span>
                <br />
                <span style={{ fontSize: '12px', color: '#666' }}>Group: {getGroupName(dataItem.groupId)}</span>
                <br />
                <span style={{ fontSize: '12px', color: '#888' }}>S/N: {dataItem.endpointDeviceSerialNumber || 'N/A'}</span>
            </div>
        </li>
    );
};

const GreasePumpMappingComponent: React.FC<Props> = ({
    groups,
    selectedGreasePumpId,
    selectedGreasePumpName,
    unassignedEndpoints,
    assignedEndpoints,
    onAssignEndpoints,
    onUnassignEndpoints
}) => {
    const localizationService = useLocalization();
    const pumpId = selectedGreasePumpId || '';
    const [lists, setLists] = React.useState<{ unassigned: any[]; assigned: any[] }>({
        unassigned: sortEndpointsByName(addListBoxFields(unassignedEndpoints)),
        assigned: sortEndpointsByName(addListBoxFields(assignedEndpoints))
    });
    const [unassignedFilter, setUnassignedFilter] = React.useState<string>('');
    const [assignedFilter, setAssignedFilter] = React.useState<string>('');

    const listsRef = React.useRef(lists);
    listsRef.current = lists;

    React.useEffect(() => {
        setLists({
            unassigned: sortEndpointsByName(addListBoxFields(unassignedEndpoints)),
            assigned: sortEndpointsByName(addListBoxFields(assignedEndpoints))
        });
    }, [unassignedEndpoints, assignedEndpoints, pumpId]);

    const getSearchText = React.useCallback((item: any) => {
        const groupName = groups?.find((group: any) => group.id === item.groupId)?.name ?? item.groupId ?? '';
        const serialNumber = item.endpointDeviceSerialNumber ?? '';

        return `${item.name ?? ''} ${groupName} ${serialNumber}`.toLowerCase();
    }, [groups]);

    const leftList = React.useMemo(() => {
        if (!unassignedFilter) {
            return lists.unassigned;
        }

        const searchValue = unassignedFilter.toLowerCase();
        return lists.unassigned.filter((item: any) => getSearchText(item).includes(searchValue));
    }, [getSearchText, lists.unassigned, unassignedFilter]);

    const rightList = React.useMemo(() => {
        if (!assignedFilter) {
            return lists.assigned;
        }

        const searchValue = assignedFilter.toLowerCase();
        return lists.assigned.filter((item: any) => getSearchText(item).includes(searchValue));
    }, [assignedFilter, getSearchText, lists.assigned]);

    const handleItemClick = (e: ListBoxItemClickEvent, which: 'left' | 'right') => {
        const key = e.dataItem[UNIQUE_KEY_FIELD] ?? getEndpointUniqueKey(e.dataItem);
        const ctrl = e.nativeEvent.ctrlKey;
        setLists(prev => {
            if (which === 'left') {
                const newLeft = prev.unassigned.map((item: any) => {
                    const k = item[UNIQUE_KEY_FIELD] ?? getEndpointUniqueKey(item);
                    const sel = k === key ? !item[SELECTED_FIELD] : (ctrl ? item[SELECTED_FIELD] : false);
                    return { ...item, [SELECTED_FIELD]: sel };
                });
                const newRight = prev.assigned.map((item: any) => ({ ...item, [SELECTED_FIELD]: false }));
                return { unassigned: newLeft, assigned: newRight };
            } else {
                const newRight = prev.assigned.map((item: any) => {
                    const k = item[UNIQUE_KEY_FIELD] ?? getEndpointUniqueKey(item);
                    const sel = k === key ? !item[SELECTED_FIELD] : (ctrl ? item[SELECTED_FIELD] : false);
                    return { ...item, [SELECTED_FIELD]: sel };
                });
                const newLeft = prev.unassigned.map((item: any) => ({ ...item, [SELECTED_FIELD]: false }));
                return { unassigned: newLeft, assigned: newRight };
            }
        });
    };

    const handleToolBarClick = (e: ListBoxToolbarClickEvent) => {
        const tool = e.toolName || '';
        const { unassigned, assigned } = listsRef.current;

        if (tool === 'transferFrom' || tool === 'transferAllFrom') {
            const toMove = tool === 'transferAllFrom' ? assigned : assigned.filter((item: any) => item[SELECTED_FIELD]);
            onUnassignEndpoints(toMove.map((item: any) => item[UNIQUE_KEY_FIELD] ?? getEndpointUniqueKey(item)));
            return;
        }

        if (!pumpId) {
            return;
        }

        const toMove = tool === 'transferAllTo' ? unassigned : unassigned.filter((item: any) => item[SELECTED_FIELD]);
        onAssignEndpoints(toMove.map((item: any) => item[UNIQUE_KEY_FIELD] ?? getEndpointUniqueKey(item)));
    };

    const [dragged, setDragged] = React.useState<{ item: any; source: 'left' | 'right' } | null>(null);

    const handleDragStart = (e: ListBoxDragEvent, source: 'left' | 'right') => {
        setDragged({ item: e.dataItem, source });
    };

    const handleDrop = (e: ListBoxDragEvent) => {
        if (!dragged) {
            return;
        }

        const result = processListBoxDragAndDrop(leftList, rightList, dragged.item, e.dataItem, UNIQUE_KEY_FIELD);
        const draggedKey = dragged.item[UNIQUE_KEY_FIELD] ?? getEndpointUniqueKey(dragged.item);
        const movedToAssigned = result.listBoxTwoData.some((item: any) => (item[UNIQUE_KEY_FIELD] ?? getEndpointUniqueKey(item)) === draggedKey);
        const movedToUnassigned = result.listBoxOneData.some((item: any) => (item[UNIQUE_KEY_FIELD] ?? getEndpointUniqueKey(item)) === draggedKey);

        if (dragged.source === 'left' && movedToAssigned && pumpId) {
            onAssignEndpoints([draggedKey]);
        }

        if (dragged.source === 'right' && movedToUnassigned) {
            onUnassignEndpoints([draggedKey]);
        }

        setDragged(null);
    };

    const onUnassignedFilterChange = (event: any) => {
        setUnassignedFilter(event.value);
    };

    const onAssignedFilterChange = (event: any) => {
        setAssignedFilter(event.value);
    };

    const onUnassignedFilterClear = (event: { preventDefault: () => void; }) => {
        event.preventDefault();
        setUnassignedFilter('');
    };

    const onAssignedFilterClear = (event: { preventDefault: () => void; }) => {
        event.preventDefault();
        setAssignedFilter('');
    };

    return (
        <div className="h-full flex flex-col">
            <div className="text-xl font-bold">Grease Pump Mapping</div>
            <div className="flex-1 flex gap-2 min-h-0">
                <div className="flex-1 flex flex-col min-h-0">
                    <h6 className="text-sm font-semibold mb-2 text-gray-600">Unassigned Endpoints</h6>
                    <div className="flex space-x-2 mb-2">
                        <Input
                            onChange={onUnassignedFilterChange}
                            value={unassignedFilter}
                            placeholder={localizationService.toLanguageString('custom.search', enMessages.custom.search)}
                        />
                        <Button
                            title="Clear"
                            disabled={!unassignedFilter}
                            onClick={onUnassignedFilterClear}
                            svgIcon={filterClearIcon}
                            style={{ width: '30px' }}
                        />
                    </div>
                    <div className="flex-1 min-h-0" style={{ minHeight: 120 }}>
                        <ListBox
                            style={{ height: '100%', width: '100%' }}
                            data={leftList}
                            textField="name"
                            valueField={UNIQUE_KEY_FIELD}
                            selectedField={SELECTED_FIELD}
                            onItemClick={(e) => handleItemClick(e, 'left')}
                            onDragStart={(e) => handleDragStart(e, 'left')}
                            onDrop={handleDrop}
                            item={(p: any) => <MyCustomItem {...p} groups={groups} />}
                            draggable
                            toolbar={() => (
                                <ListBoxToolbar
                                    tools={['transferTo', 'transferFrom', 'transferAllTo', 'transferAllFrom']}
                                    data={leftList}
                                    dataConnected={rightList}
                                    onToolClick={handleToolBarClick}
                                />
                            )}
                        />
                    </div>
                </div>
                <div className="flex-1 flex flex-col min-h-0">
                    <h6 className="text-sm font-semibold mb-2 text-gray-600">{selectedGreasePumpName}</h6>
                    <div className="flex space-x-2 mb-2">
                        <Input
                            onChange={onAssignedFilterChange}
                            value={assignedFilter}
                            placeholder={localizationService.toLanguageString('custom.search', enMessages.custom.search)}
                        />
                        <Button
                            title="Clear"
                            disabled={!assignedFilter}
                            onClick={onAssignedFilterClear}
                            svgIcon={filterClearIcon}
                            style={{ width: '30px' }}
                        />
                    </div>
                    <div className="flex-1 min-h-0" style={{ minHeight: 120 }}>
                        <ListBox
                            style={{ height: '100%', width: '100%' }}
                            data={rightList}
                            textField="name"
                            valueField={UNIQUE_KEY_FIELD}
                            selectedField={SELECTED_FIELD}
                            onItemClick={(e) => handleItemClick(e, 'right')}
                            onDragStart={(e) => handleDragStart(e, 'right')}
                            onDrop={handleDrop}
                            item={(p: any) => <MyCustomItem {...p} groups={groups} />}
                            draggable
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GreasePumpMappingComponent;
